import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Wheel } from './components/Wheel';
import { CompoundingChart, type FlywheelEvent } from './components/CompoundingChart';
import { BRAIN, COMPOUNDING, CTA, HEADER, HINT, INTRO, MODEL_UPGRADE, STAGES } from './flywheel.content';

/* ---- small branded buttons ---- */
function PrimaryButton({ children, onClick, disabled }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-11 items-center gap-2 rounded-fd-sm border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist text-[14px] font-semibold text-[oklch(0.16_0.005_95)] shadow-[2px_2px_0_0_var(--fd-text)] transition-transform hover:-translate-y-px active:translate-y-px active:shadow-[1px_1px_0_0_var(--fd-text)] disabled:pointer-events-none disabled:opacity-50"
    >
      {children}
    </button>
  );
}
function SecondaryButton({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex h-11 items-center gap-2 rounded-fd-sm border border-fd-border bg-fd-paper px-4 font-geist text-[14px] font-medium text-fd-text transition-colors hover:bg-fd-surface-1"
    >
      {children}
    </button>
  );
}
function GhostButton({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex h-11 items-center rounded-fd-sm px-3 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:text-fd-text">
      {children}
    </button>
  );
}

export default function FlywheelApp() {
  const systemRM = useReducedMotion();
  const [userRM, setUserRM] = useState(false);
  const reducedMotion = Boolean(systemRM) || userRM;

  const [started, setStarted] = useState(false);
  const [laps, setLaps] = useState(0);
  const [focused, setFocused] = useState<number | null>(null);
  const [events, setEvents] = useState<FlywheelEvent[]>([]);
  const [lapToken, setLapToken] = useState(0);
  const [boostToken, setBoostToken] = useState(0);
  const [highlightRotation, setHighlightRotation] = useState(0);
  const [modelMsg, setModelMsg] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const boosts = useMemo(() => events.filter((e) => e === 'boost').length, [events]);
  const brainFill = Math.min(1, 1 - Math.pow(0.8, laps + boosts * 1.5));
  const lapDuration = 1.15;

  // Slide the highlight to a stage's segment by the shortest path around the ring.
  const moveHighlightTo = (deg: number) =>
    setHighlightRotation((cur) => {
      let delta = (((deg - (cur % 360)) % 360) + 360) % 360;
      if (delta > 180) delta -= 360;
      return cur + delta;
    });
  const focusStage = (i: number) => {
    if (focused === i) {
      setFocused(null);
      moveHighlightTo(0);
      return;
    }
    setFocused(i);
    moveHighlightTo(i * 72);
  };
  const backToAnalysis = () => {
    setFocused(null);
    moveHighlightTo(0);
  };

  useEffect(() => {
    const onFs = () => setPresenting(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const togglePresent = () => {
    const el = rootRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const start = () => setStarted(true);
  const runLap = () => {
    setLaps((l) => l + 1);
    setEvents((e) => [...e, 'lap']);
    setLapToken((t) => t + 1);
    setHighlightRotation((r) => r + 360); // travel a full turn of the loop
  };
  const modelUpgrade = () => {
    setEvents((e) => [...e, 'boost']);
    setBoostToken((t) => t + 1);
    setModelMsg(true);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setModelMsg(false), 6000);
  };
  const reset = () => {
    setLaps(0);
    setEvents([]);
    setFocused(null);
    setLapToken(0);
    setBoostToken(0);
    setHighlightRotation(0);
    setModelMsg(false);
  };

  const focusedStage = focused === null ? null : STAGES[focused];

  return (
    <div ref={rootRef} className="min-h-[100dvh] bg-fd-bg font-geist text-fd-text antialiased">
      {/* header */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-fd-border bg-fd-bg/85 px-6 backdrop-blur md:px-8">
        <div
          className="grid h-7 w-7 place-items-center rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent font-geist-mono text-[14px] font-bold text-[oklch(0.16_0.005_95)]"
          style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
        >
          A
        </div>
        <div className="leading-tight">
          <div className="text-[13px] font-bold uppercase tracking-[-0.005em] text-fd-text">AI Brand Lab</div>
          <div className="font-geist-mono text-[10px] uppercase tracking-[0.08em] text-fd-text-subtle">The compounding loop</div>
        </div>
        <button
          onClick={togglePresent}
          className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-fd-sm border border-fd-border bg-fd-paper px-3 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
        >
          {presenting ? 'Exit' : 'Present'}
        </button>
      </header>

      <main
        className={`mx-auto max-w-[1240px] px-6 py-8 md:px-8 md:py-10 ${presenting ? 'flex min-h-[calc(100dvh-3.5rem)] flex-col justify-center' : ''}`}
        style={{ backgroundImage: 'radial-gradient(var(--fd-grid) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
      >
        {/* hero */}
        <div className={`max-w-[58ch] ${presenting ? 'hidden' : ''}`}>
          <div className="font-geist-mono text-[11px] font-medium uppercase tracking-[0.14em] text-fd-teal">{HEADER.eyebrow}</div>
          <h1 className="mt-3 text-balance text-[1.9rem] font-semibold leading-[1.08] tracking-[-0.02em] md:text-[2.6rem]">{HEADER.title}</h1>
          <p className="mt-4 text-[15px] leading-relaxed text-fd-text-muted md:text-base">{HEADER.sub}</p>
        </div>

        {/* interactive */}
        <div className={`grid items-start gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 ${presenting ? 'mt-0' : 'mt-8 lg:mt-10'}`}>
          {/* wheel + controls */}
          <div className="flex flex-col items-center">
            <Wheel
              stages={STAGES}
              started={started}
              focusedIndex={focused}
              onFocus={focusStage}
              brainFill={brainFill}
              lapToken={lapToken}
              boostToken={boostToken}
              highlightRotation={highlightRotation}
              lapDuration={lapDuration}
              reducedMotion={reducedMotion}
              large={presenting}
            />

            {/* controls */}
            <div className="mt-6 w-full max-w-[520px]">
              {!started ? (
                <div className="flex flex-col items-center gap-4 text-center">
                  <p className="max-w-[44ch] text-[14px] leading-relaxed text-fd-text-muted">{INTRO.oldWayLine}</p>
                  <PrimaryButton onClick={start}>{INTRO.cue}</PrimaryButton>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center justify-center gap-2.5">
                    <PrimaryButton onClick={runLap}>{COMPOUNDING.runLabel}</PrimaryButton>
                    <SecondaryButton onClick={modelUpgrade}>{MODEL_UPGRADE.button}</SecondaryButton>
                    <GhostButton onClick={reset}>Reset</GhostButton>
                  </div>
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => setUserRM((v) => !v)}
                      className="font-geist-mono text-[10.5px] uppercase tracking-[0.08em] text-fd-text-subtle transition-colors hover:text-fd-text-muted"
                    >
                      {reducedMotion ? '● reduced motion' : '○ reduced motion'}
                    </button>
                  </div>
                  {focused === null && (
                    <p className="text-center font-geist text-[12.5px] leading-relaxed text-fd-text-subtle">{HINT}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* panel */}
          <div className="relative min-h-[440px] overflow-hidden rounded-[1.25rem] border border-fd-border bg-fd-paper p-6 shadow-[0_18px_40px_-22px_oklch(0.16_0.005_95_/_0.22)] md:p-8">
            {focusedStage && (
              <button
                onClick={backToAnalysis}
                aria-label="Back to the analysis"
                className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full border border-fd-border bg-fd-paper text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <path d="M3 3l8 8M11 3l-8 8" />
                </svg>
              </button>
            )}
            <AnimatePresence mode="wait">
              {focusedStage ? (
                <motion.div key={focusedStage.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.28 }}>
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex items-center rounded-fd-sm border-[1.5px] border-fd-text bg-fd-accent px-2 py-0.5 font-geist-mono text-[11px] font-bold text-[oklch(0.16_0.005_95)]">
                      STEP {String((focused ?? 0) + 1).padStart(2, '0')}
                    </span>
                    <span className="font-geist-mono text-[11px] uppercase tracking-[0.1em] text-fd-text-subtle">{focusedStage.tag}</span>
                  </div>
                  <h2 className="mt-4 text-[1.4rem] font-semibold leading-tight tracking-[-0.01em]">{focusedStage.title}</h2>
                  <p className="mt-3 text-[15px] leading-relaxed text-fd-text-muted">{focusedStage.body}</p>
                  <div className="mt-5 border-l-2 border-fd-teal pl-4">
                    <div className="font-geist-mono text-[10.5px] uppercase tracking-[0.1em] text-fd-teal">For you</div>
                    <p className="mt-1.5 text-[14.5px] leading-relaxed text-fd-text">{focusedStage.forYou}</p>
                  </div>
                  <button onClick={backToAnalysis} className="mt-7 inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-fd-paper px-3 py-1.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text">
                    ← Back to the analysis
                  </button>
                </motion.div>
              ) : (
                <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.28 }}>
                  <div className="font-geist-mono text-[11px] uppercase tracking-[0.1em] text-fd-text-subtle">{BRAIN.tag}</div>
                  <h2 className="mt-3 text-[1.4rem] font-semibold leading-tight tracking-[-0.01em]">{BRAIN.title}</h2>
                  <p className="mt-3 text-[15px] leading-relaxed text-fd-text-muted">{BRAIN.body}</p>

                  {/* readout */}
                  <div className="mt-6 grid grid-cols-[auto_1fr] items-end gap-x-5 gap-y-2">
                    <div>
                      <div className="font-geist-mono text-[10.5px] uppercase tracking-[0.1em] text-fd-text-subtle">{COMPOUNDING.lapsLabel}</div>
                      <div className="font-geist-mono text-[2rem] font-semibold leading-none tabular-nums">{laps}</div>
                    </div>
                    <div className="pb-1">
                      <div className="mb-1 flex items-center justify-between font-geist-mono text-[10.5px] uppercase tracking-[0.08em] text-fd-text-subtle">
                        <span>{COMPOUNDING.brainLabel}</span>
                        <span className="tabular-nums">{Math.round(brainFill * 100)}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-fd-surface-2">
                        <motion.div className="h-full rounded-full bg-fd-teal" animate={{ width: `${Math.round(brainFill * 100)}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-5">
                    <CompoundingChart events={events} reducedMotion={reducedMotion} />
                    <div className="mt-2 flex items-center gap-4 font-geist-mono text-[10.5px] uppercase tracking-[0.06em]">
                      <span className="flex items-center gap-1.5 text-fd-text">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--fd-text)' }} />
                        {COMPOUNDING.compoundsLabel}
                      </span>
                      <span className="flex items-center gap-1.5 text-fd-text-subtle">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--fd-coral)' }} />
                        {COMPOUNDING.resetsLabel}
                      </span>
                    </div>
                  </div>

                  <p className="mt-5 text-[13.5px] leading-relaxed text-fd-text-muted">{started ? COMPOUNDING.caption : BRAIN.forYou}</p>

                  <AnimatePresence>
                    {modelMsg && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="rounded-fd-sm border border-fd-accent-line bg-fd-accent-soft p-3.5">
                          <div className="font-geist-mono text-[10.5px] uppercase tracking-[0.1em] text-fd-text">{MODEL_UPGRADE.button}</div>
                          <p className="mt-1.5 text-[13.5px] leading-relaxed text-fd-text">{MODEL_UPGRADE.effect}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* close */}
        <div className={`mt-14 flex flex-col items-start gap-4 border-t border-fd-border pt-8 sm:flex-row sm:items-center sm:justify-between ${presenting ? 'hidden' : ''}`}>
          <p className="max-w-[40ch] text-[15px] font-medium leading-snug text-fd-text">{CTA.line}</p>
          <PrimaryButton onClick={() => { window.location.href = CTA.href; }}>{CTA.button}</PrimaryButton>
        </div>
      </main>
    </div>
  );
}
